import React from 'react';
import { Box, Divider, Link, Stack, Typography } from '@mui/material';
import PageSeo from '../seo/PageSeo';
import { DEFAULT_OG_IMAGE, SITE_NAME, SITE_URL, pagePathToUrl } from '../seo/constants';
import PageLayout from '../components/PageLayout';
import TikTokVideoCta from '../components/TikTokVideoCta';
import { GROOMING_TIKTOK_VIDEOS, groomingTikTokVideoJsonLd } from '../site/groomingVideos';
import { SOCIAL_LINKS } from '../site/contact';

const sourceLinkSx = {
    color: 'primary.main',
    textDecoration: 'underline',
    '&:hover': { color: 'primary.dark' },
};

const PAGE_PATH = '/grooming';

const groomingVideo = (id: string) =>
    GROOMING_TIKTOK_VIDEOS.find((video) => video.id === id)!;

const Grooming: React.FC = () => {
    const pageTitle = 'Standard Poodle Grooming Guide | Austin, TX | Galia Standard Poodles';
    const pageDescription =
        'How to groom a standard poodle: line brushing, nail trimming, bathing, and ear care — plus TikTok video tutorials on products, clipper length, and more from Galia Standard Poodles in Austin, TX.';
    const pageUrl = pagePathToUrl(PAGE_PATH);

    return (
        <>
            <PageSeo
                title={pageTitle}
                description={pageDescription}
                path={PAGE_PATH}
                image={DEFAULT_OG_IMAGE}
                imageAlt="Calcifer, black AKC champion standard poodle in show stack pose"
                jsonLd={[
                    {
                        '@context': 'https://schema.org',
                        '@type': 'WebPage',
                        name: pageTitle,
                        description: pageDescription,
                        url: pageUrl,
                        inLanguage: 'en-US',
                        isPartOf: {
                            '@type': 'WebSite',
                            name: SITE_NAME,
                            url: SITE_URL,
                        },
                        about: [
                            { '@type': 'Thing', name: 'Standard Poodle grooming' },
                            { '@type': 'Thing', name: 'Dog coat care' },
                            { '@type': 'Thing', name: 'Line brushing' },
                        ],
                    },
                    {
                        '@context': 'https://schema.org',
                        '@type': 'BreadcrumbList',
                        itemListElement: [
                            {
                                '@type': 'ListItem',
                                position: 1,
                                name: 'Home',
                                item: `${SITE_URL}/`,
                            },
                            {
                                '@type': 'ListItem',
                                position: 2,
                                name: 'Grooming',
                                item: pageUrl,
                            },
                        ],
                    },
                    {
                        '@context': 'https://schema.org',
                        '@type': 'ItemList',
                        name: 'Galia Standard Poodles grooming TikTok tutorials',
                        description: 'Educational grooming videos from Galia Standard Poodles on TikTok.',
                        itemListElement: groomingTikTokVideoJsonLd,
                    },
                ]}
            />
            <PageLayout>
                <Typography component="h1" variant="h4" gutterBottom>
                    Grooming
                </Typography>
                <Typography variant="body1" paragraph>
                    Standard Poodles have a continuously growing, curly coat that rewards consistent care. The
                    guidance below summarizes what every poodle owner should know — drawn from reputable sources,
                    not opinion. At Galia, we begin grooming desensitization with our puppies at three weeks so
                    new families have a head start on calm, cooperative grooming at home.
                </Typography>

                <Box
                    sx={{
                        mt: 2.5,
                        mb: 1,
                        p: { xs: 2, md: 2.5 },
                        borderRadius: 2,
                        bgcolor: '#f9f9f9',
                        border: '1px solid rgba(15, 23, 42, 0.08)',
                    }}
                >
                    <Typography component="h2" variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                        Watch our grooming tutorials
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph sx={{ mb: 2 }}>
                        Prefer video? We post step-by-step grooming guides on TikTok — products, nail trims,
                        clipper length, and more.
                    </Typography>
                    <Stack spacing={1.5}>
                        {GROOMING_TIKTOK_VIDEOS.map((video) => (
                            <TikTokVideoCta
                                key={video.id}
                                title={video.title}
                                description={video.description}
                                url={video.url}
                            />
                        ))}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        Follow{' '}
                        <Link
                            href={SOCIAL_LINKS.tiktok}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={sourceLinkSx}
                        >
                            @galiapoodles on TikTok
                        </Link>{' '}
                        for more.
                    </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography component="h2" variant="h5" gutterBottom>
                    Grooming Desensitization
                </Typography>
                <Typography variant="body1" paragraph>
                    Dogs who learn to tolerate handling early are far easier to groom for life. The{' '}
                    <Link
                        href="https://www.akc.org/expert-advice/training/preventing-body-sensitivity/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={sourceLinkSx}
                    >
                        AKC
                    </Link>{' '}
                    recommends starting handling exercises while puppies are young: gently touch paws, ears, belly,
                    and tail while pairing each touch with praise and treats. Work up slowly to brushes, nail tools,
                    and brief grooming sessions rather than waiting for a full coat maintenance crisis.
                </Typography>
                <Typography variant="body1" paragraph>
                    The{' '}
                    <Link
                        href="https://www.akc.org/expert-advice/dog-breeding/setting-puppies-success-handling-grooming/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={sourceLinkSx}
                    >
                        AKC
                    </Link>{' '}
                    also advises breeders and new owners to keep grooming sessions{' '}
                    <strong>short, positive, and frequent</strong> — a few brush strokes or one nail at a time is
                    enough early on. High-value treats (for example, a little yogurt or canned food on a spoon to
                    lick while you groom) help puppies associate grooming with good things. Introduce tubs, tables,
                    dryers, and tools gradually: let the puppy sniff equipment, hear sounds from a distance, and
                    earn treats before any real grooming pressure is applied.
                </Typography>
                <Typography variant="body1" paragraph>
                    Never push through fear. If your dog shows stress, end on a success and try again later with an
                    easier step. One painful mat removal or clipped quick can create lasting grooming aversion.
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Typography component="h2" variant="h5" gutterBottom>
                    Line Brushing for Curly Coats
                </Typography>
                <Typography variant="body1" paragraph>
                    Surface brushing — running a brush over the top of the coat — is not enough for a Standard Poodle.
                    Mats often form at the skin while the outer coat still looks tidy. <strong>Line brushing</strong>{' '}
                    is the professional method used to brush curly-coated dogs all the way from skin to tip.
                </Typography>
                <Typography variant="body1" paragraph>
                    The{' '}
                    <Link
                        href="https://www.akc.org/expert-advice/health/how-to-groom-a-dog/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={sourceLinkSx}
                    >
                        AKC
                    </Link>{' '}
                    stresses brushing thoroughly before bathing to remove dead hair and mats that tighten when wet.
                    For poodles, that means:
                </Typography>
                <Box component="ol" sx={{ pl: 3, mb: 2, '& li': { mb: 1 } }}>
                    <Typography component="li" variant="body1">
                        <strong>Part the coat</strong> with your free hand to expose a line of skin.
                    </Typography>
                    <Typography component="li" variant="body1">
                        <strong>Brush outward</strong> from the skin to the ends of the hair in that section, using
                        a slicker brush sized for your dog.
                    </Typography>
                    <Typography component="li" variant="body1">
                        <strong>Move systematically</strong> across the body — part, brush, advance — rather than
                        brushing randomly over the surface.
                    </Typography>
                    <Typography component="li" variant="body1">
                        <strong>Comb-check each section</strong> with a metal greyhound comb. If the comb snags,
                        that section is not finished.
                    </Typography>
                </Box>
                <Typography variant="body1" paragraph>
                    Pay extra attention to friction zones where mats start first: behind the ears, armpits, collar
                    line, groin, and under the tail. Curly-coated dogs generally need thorough brushing far more
                    often than short-coated breeds — many poodle owners brush daily or every other day.
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Typography component="h2" variant="h5" gutterBottom>
                    Matting and Prevention
                </Typography>
                <Typography variant="body1" paragraph>
                    Mats are tangled clumps of hair that pull on the skin and can trap moisture, cause irritation,
                    and lead to hot spots. Prevention is always easier than removal.
                </Typography>
                <Typography variant="body1" paragraph>
                    Key prevention steps supported by grooming and veterinary guidance:
                </Typography>
                <Box component="ul" sx={{ pl: 3, mb: 2, '& li': { mb: 1 } }}>
                    <Typography component="li" variant="body1">
                        <strong>Line brush and comb-check regularly</strong> — not just when the coat looks fluffy
                        on top.
                    </Typography>
                    <Typography component="li" variant="body1">
                        <strong>Never bathe a tangled coat.</strong> The{' '}
                        <Link
                            href="https://www.akc.org/expert-advice/health/the-soap-and-water-of-dog-ownership/"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={sourceLinkSx}
                        >
                            AKC
                        </Link>{' '}
                        and coat-care experts agree: brush thoroughly before bathing. Water tightens existing
                        tangles into solid mats.
                    </Typography>
                    <Typography component="li" variant="body1">
                        <strong>Dry the coat fully after bathing.</strong> Damp, compressed curls mat more easily
                        as they settle.
                    </Typography>
                    <Typography component="li" variant="body1">
                        <strong>Address small tangles early</strong> with gentle work from the tips inward — do not
                        yank through painful mats at the skin.
                    </Typography>
                </Box>
                <Typography variant="body1" paragraph>
                    Severe matting may require professional help. In some cases, a groomer or veterinarian may
                    recommend shaving affected areas for the dog&apos;s comfort and skin health. Regular professional
                    grooming every four to six weeks is typical for maintained poodle coats.
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Typography component="h2" variant="h5" gutterBottom>
                    Nail Trimming: Clippers, Dremel, and the Quick
                </Typography>
                <Typography variant="body1" paragraph>
                    Nail care is essential for posture, traction, and long-term joint comfort. The{' '}
                    <Link
                        href="https://www.akc.org/expert-advice/health/how-to-trim-dogs-nails-safely/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={sourceLinkSx}
                    >
                        AKC
                    </Link>{' '}
                    explains that a dog&apos;s nail has a hard outer shell and a living inner structure called the{' '}
                    <strong>quick</strong>, which contains blood vessels and nerves. Cutting into the quick is painful
                    and will bleed.
                </Typography>
                <Typography variant="body1" paragraph>
                    On light-colored nails, the pink quick is often visible — trim only the tip, staying before the
                    quick. On dark nails, trim very small amounts at a time and watch for a chalky white ring that
                    signals you are getting close. Keep styptic powder on hand in case of an accidental nick.
                </Typography>
                <Typography variant="body1" paragraph>
                    You can use dog nail clippers or a <strong>dog-safe grinder (Dremel)</strong>. The AKC notes
                    that grinders remove a little at a time, smooth rough edges, and may feel less stressful for some
                    dogs than clippers — though you should introduce the tool gradually, keep long coat hair away
                    from the grinder, and avoid heat buildup on the nail.
                </Typography>
                <Typography variant="body1" paragraph>
                    <strong>Receding the quick:</strong> When nails have been allowed to grow long, the quick extends
                    toward the tip. The AKC states that <strong>regular, small nail trims</strong> encourage the quick
                    to recede over time, making safe maintenance easier. Frequent tiny trims (some owners trim weekly)
                    are more effective than rare large cuts. The AKC&apos;s{' '}
                    <Link
                        href="https://www.akc.org/expert-advice/family-dog/dog-nail-care-routine/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={sourceLinkSx}
                    >
                        nail care guidance
                    </Link>{' '}
                    also recommends taking only a small amount from each side and the tip rather than cutting straight
                    across in one aggressive clip when working around an extended quick.
                </Typography>
                <Box sx={{ mt: 2, mb: 1 }}>
                    <TikTokVideoCta compact {...groomingVideo('nail-trimming')} />
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography component="h2" variant="h5" gutterBottom>
                    Bathing and Blow Drying
                </Typography>
                <Typography variant="body1" paragraph>
                    The{' '}
                    <Link
                        href="https://www.akc.org/expert-advice/health/its-bath-time-towels-brushes-dog-shampoo-and-more/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={sourceLinkSx}
                    >
                        AKC
                    </Link>{' '}
                    recommends brushing before the bath, using warm (not hot) water, thorough rinsing until all
                    residue is gone, and protecting eyes and ears from soap and water. Many owners use shampoo
                    formulated for dogs; we choose products based on each dog&apos;s coat type and needs — sometimes
                    that means a dog shampoo, and sometimes a human product such as Pantene works well for a
                    particular coat. What matters most is a gentle formula, a complete rinse, and watching how your
                    dog&apos;s skin and coat respond. Cotton balls in the ears can help keep bath water out.
                </Typography>
                <Box sx={{ mt: 2, mb: 1 }}>
                    <TikTokVideoCta compact {...groomingVideo('recommended-products')} />
                </Box>
                <Typography variant="body1" paragraph>
                    After bathing, towel-dry vigorously, then finish drying the coat. For poodles, <strong>air drying
                    alone</strong> often leaves tight curls that mat against the skin. Professional groomers typically:
                </Typography>
                <Box component="ol" sx={{ pl: 3, mb: 2, '& li': { mb: 1 } }}>
                    <Typography component="li" variant="body1">
                        Remove most moisture with towels, then use a <strong>high-velocity (force) dryer</strong> to
                        blow water out of the dense coat down to the skin.
                    </Typography>
                    <Typography component="li" variant="body1">
                        Follow with <strong>fluff drying</strong> — brushing the coat while warm air flows through
                        it — so curls are stretched and separated rather than drying in tight clumps.
                    </Typography>
                </Box>
                <Typography variant="body1" paragraph>
                    The AKC advises using a dryer made for dogs on a <strong>warm, gentle setting</strong>, held at
                    least a foot away from the skin, and monitoring your dog for discomfort. Protect sensitive ears
                    from loud airflow. Never use high heat close to the skin. If you are new to force dryers, ask a
                    professional groomer to demonstrate safe technique for your dog&apos;s coat length and temperament.
                </Typography>
                <Box sx={{ mt: 2, mb: 1 }}>
                    <TikTokVideoCta compact {...groomingVideo('clipper-length')} />
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography component="h2" variant="h5" gutterBottom>
                    Ear Care: Cleaning, Trimming, and Infections
                </Typography>
                <Typography variant="body1" paragraph>
                    Poodles can be prone to ear problems when moisture, debris, or inflammation builds in the ear
                    canal. Routine ear care should be thoughtful — not aggressive.
                </Typography>
                <Typography variant="body1" paragraph>
                    <strong>Cleaning:</strong>{' '}
                    <Link
                        href="https://www.vet.cornell.edu/departments-centers-and-institutes/riney-canine-health-center/canine-health-topics/how-clean-your-dogs-ears"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={sourceLinkSx}
                    >
                        Cornell University&apos;s Riney Canine Health Center
                    </Link>{' '}
                    and{' '}
                    <Link
                        href="https://vcahospitals.com/know-your-pet/ear-cleaning-in-dogs"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={sourceLinkSx}
                    >
                        VCA Animal Hospitals
                    </Link>{' '}
                    note that not every healthy dog needs frequent ear cleaning. Over-cleaning can irritate the canal.
                    Clean when you see debris or odor, or when your veterinarian recommends a maintenance schedule —
                    especially after swimming or bathing. Use a veterinarian-recommended ear cleaner (avoid alcohol or
                    hydrogen peroxide in inflamed ears), fill the canal, massage the base, let the dog shake, then wipe
                    debris with cotton pads. <strong>Do not use cotton swabs</strong> deep in the canal.
                </Typography>
                <Typography variant="body1" paragraph>
                    <strong>Hair in the ears:</strong> Whether to pluck ear canal hair is debated. The{' '}
                    <Link
                        href="https://www.msdvetmanual.com/dog-owners/ear-disorders-of-dogs/ear-infections-and-otitis-externa-in-dogs"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={sourceLinkSx}
                    >
                        MSD Veterinary Manual
                    </Link>{' '}
                    notes that in some cases, trimming or plucking hair near the canal may improve airflow and reduce
                    humidity — but this should be discussed with your veterinarian first. Plucking can cause
                    micro-trauma and inflammation in healthy ears. Many veterinarians now recommend leaving ear hair
                    alone unless your dog has recurrent infections and your vet advises a specific plan.
                </Typography>
                <Typography variant="body1" paragraph>
                    <strong>Signs of infection</strong> include redness, odor, discharge, head shaking, scratching, or
                    pain when the ear is touched. If you notice these signs, see your veterinarian before deep
                    cleaning or plucking — the ear may need medication, and cleaning inflamed ears incorrectly can
                    worsen the problem.
                </Typography>

                <Divider sx={{ my: 3 }} />

                <Typography component="h2" variant="h5" gutterBottom>
                    What We Do at Galia
                </Typography>
                <Typography variant="body1" paragraph>
                    We start grooming desensitization early — including brief handling, brushing introductions, and
                    positive experiences with tools — so our puppies are prepared for lifelong coat care. We are
                    always happy to answer questions about maintaining your poodle&apos;s coat between professional
                    grooms.
                </Typography>

                <Box
                    sx={{
                        bgcolor: '#f9f9f9',
                        p: 2,
                        borderLeft: '4px solid',
                        borderColor: 'primary.main',
                        borderRadius: 2,
                        mt: 3,
                    }}
                >
                    <Typography variant="body2" color="text.secondary">
                        <strong>Sources:</strong> Grooming information on this page is drawn primarily from the{' '}
                        <Link
                            href="https://www.akc.org/expert-advice/health/how-to-trim-dogs-nails-safely/"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={sourceLinkSx}
                        >
                            AKC
                        </Link>{' '}
                        (nail care, bathing, home grooming, handling, and puppy grooming),{' '}
                        <Link
                            href="https://www.vet.cornell.edu/departments-centers-and-institutes/riney-canine-health-center/canine-health-topics/how-clean-your-dogs-ears"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={sourceLinkSx}
                        >
                            Cornell Riney Canine Health Center
                        </Link>{' '}
                        and{' '}
                        <Link
                            href="https://vcahospitals.com/know-your-pet/ear-cleaning-in-dogs"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={sourceLinkSx}
                        >
                            VCA Animal Hospitals
                        </Link>{' '}
                        (ear care), and the{' '}
                        <Link
                            href="https://www.msdvetmanual.com/dog-owners/ear-disorders-of-dogs/ear-infections-and-otitis-externa-in-dogs"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={sourceLinkSx}
                        >
                            MSD Veterinary Manual
                        </Link>{' '}
                        (ear hair and infection prevention). Line brushing and force-dry technique descriptions reflect
                        standard professional grooming practice for curly-coated breeds. This page is for educational
                        purposes only and does not constitute veterinary advice.
                    </Typography>
                </Box>
            </PageLayout>
        </>
    );
};

export default Grooming;
